import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useAuth } from "../../contexts/AuthContext";
import {
  RELEASES,
  getUpdateLink,
} from "../../components/WhatsNew/updatesConfig";
import "./Updates.scss";

const TYPE_META = {
  feature: { label: "New" },
  improvement: { label: "Improved" },
  performance: { label: "Performance" },
  design: { label: "Design" },
  fix: { label: "Fixed" },
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "feature", label: "New" },
  { key: "improvement", label: "Improved" },
  { key: "performance", label: "Performance" },
  { key: "design", label: "Design" },
  { key: "fix", label: "Fixed" },
];

const UpdatesPage = () => {
  const { hasRole } = useAuth();
  const [activeFilter, setActiveFilter] = useState("all");
  const [activeVersion, setActiveVersion] = useState(RELEASES[0]?.version);

  const visibleReleases = useMemo(() => {
    if (activeFilter === "all") return RELEASES;
    return RELEASES.map((release) => ({
      ...release,
      items: release.items.filter((item) => item.type === activeFilter),
    })).filter((release) => release.items.length > 0);
  }, [activeFilter]);

  const handleJumpToVersion = (version) => {
    setActiveVersion(version);
    const el = document.getElementById(`release-${version}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="updates-page">
      <div className="updates-page-header">
        <h1>What's New</h1>
        <p>The latest improvements and new features in the app.</p>

        <div className="updates-filters">
          {FILTERS.map((filter) => (
            <button
              key={filter.key}
              type="button"
              className={`filter-pill ${activeFilter === filter.key ? "active" : ""}`}
              onClick={() => setActiveFilter(filter.key)}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      <div className="updates-page-body">
        <aside className="updates-sidebar">
          <div className="sidebar-label">Versions</div>
          <nav className="sidebar-nav">
            {RELEASES.map((release) => (
              <button
                key={release.version}
                type="button"
                className={`sidebar-item ${activeVersion === release.version ? "active" : ""}`}
                onClick={() => handleJumpToVersion(release.version)}
              >
                <span className="sidebar-version">{release.version}</span>
                <span className="sidebar-date">{release.date}</span>
              </button>
            ))}
          </nav>
        </aside>

        <main className="updates-content">
          {visibleReleases.length === 0 ? (
            <div className="updates-empty">
              <p>No updates match this filter yet.</p>
            </div>
          ) : (
            visibleReleases.map((release) => {
              const link = getUpdateLink(release, hasRole);
              return (
                <section
                  className="release-block"
                  id={`release-${release.version}`}
                  key={release.version}
                >
                  <div className="release-meta">
                    <span className="release-version">{release.version}</span>
                    <span className="release-date">{release.date}</span>
                  </div>

                  <p className="release-summary">{release.summary}</p>

                  {link && (
                    <Link to={link.to} className="release-cta">
                      {link.label} <Icon icon="mdi:arrow-right" />
                    </Link>
                  )}

                  <div className="release-items">
                    {release.items.map((item, idx) => {
                      const meta = TYPE_META[item.type] || { label: "Update" };
                      return (
                        <div className="release-item" key={idx}>
                          <span className={`type-tag type-${item.type}`}>
                            {meta.label}
                          </span>
                          <div className="release-item-text">
                            <div className="item-heading">{item.heading}</div>
                            <p>{item.body}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })
          )}
        </main>
      </div>
    </div>
  );
};

export default UpdatesPage;
